/**
 * A story that plays while it is still being made (docs/STORY-STREAMING.md, the player's side).
 *
 * The backend publishes the story part by part: after part k is written, `bundle.json` is
 * rewritten (atomically) with scenes 1…k and a `stream` block, and the "Making your story…"
 * screen opens the player on part 1. Three things live here, all pure or port-injected so they
 * run headless in vitest:
 *
 * 1. **`streamOf` / `isOpen` / `expectsMore`** — the `stream` block read defensively. No block
 *    means complete: every old bundle and every final one.
 * 2. **`mergeGrownBundle`** — a newer revision folded into the bundle the player already holds,
 *    *in place* (the runner, the loader and the renderer all hold that one object). It only ever
 *    adds: a published scene is final, a registered asset id keeps its pack frame, and the cast
 *    and prop tables are swapped only at the next scene load (`applyEntities`), never under a
 *    scene that is already built.
 * 3. **`watchBundle`** — the poll. `bundle.json` is served `no-cache` with an ETag, so a poll
 *    that finds nothing new is one 304; a grown bundle is one ~70 KB gzipped body. It polls
 *    faster while the child is waiting at the end of the ready parts, slowly while nothing is
 *    coming soon (paused, every part here, or silent for `STREAM_STALL_MS`), backs off after
 *    errors, and stops by itself once the story is complete, failed or gone.
 */

import type { Bundle, BundleStream } from '@/schema/story'

/* ---------------------------------------------------------------- state */

/** The `stream` block in the player's words; `complete` when the bundle has none. */
export interface StreamInfo {
  rev: number
  partsReady: number
  partsTotal: number
  state: 'streaming' | 'paused' | 'failed' | 'complete'
  /** ISO timestamp the story goes on at, while `paused`. */
  resumeAt: string | null
}

const posInt = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) && v >= 1 ? Math.floor(v) : null)

/**
 * What a bundle says about its own completeness. No block (or one this player cannot read) is
 * complete, with every scene it has ready: a story plays as it always did.
 */
export function streamOf(b: Pick<Bundle, 'scenes'> & { stream?: BundleStream | null | undefined }): StreamInfo {
  const scenes = Array.isArray(b?.scenes) ? b.scenes.length : 0
  const s = b?.stream as Partial<BundleStream> | null | undefined
  const state = s?.state
  if (!s || typeof s !== 'object' || (state !== 'streaming' && state !== 'paused' && state !== 'failed')) {
    return { rev: 0, partsReady: scenes, partsTotal: scenes, state: 'complete', resumeAt: null }
  }
  const partsReady = posInt(s.parts_ready) ?? scenes
  // `failed` promises nothing more; a total below what is here is a total of what is here.
  const partsTotal = state === 'failed' ? partsReady : Math.max(partsReady, posInt(s.parts_total) ?? partsReady)
  return {
    rev: posInt(s.rev) ?? partsReady,
    partsReady,
    partsTotal,
    state,
    resumeAt: typeof s.resume_at === 'string' && s.resume_at.length > 0 ? s.resume_at : null,
  }
}

/** The story is still being made: `streaming`, or `paused` until the usage window reopens. */
export function isOpen(s: StreamInfo): boolean {
  return s.state === 'streaming' || s.state === 'paused'
}

/**
 * More parts will come. An open stream whose every part is already here (the last part was
 * published a moment before the final bundle) has nothing to wait for: the story ends as usual.
 */
export function expectsMore(s: StreamInfo): boolean {
  return isOpen(s) && s.partsReady < s.partsTotal
}

/** Two stream states the HUD would draw the same (the poll pushes one per revision). */
export function sameStream(a: StreamInfo | null | undefined, b: StreamInfo | null | undefined): boolean {
  if (!a || !b) return a === b
  return a.rev === b.rev && a.partsReady === b.partsReady && a.partsTotal === b.partsTotal && a.state === b.state && a.resumeAt === b.resumeAt
}

/* ---------------------------------------------------------------- merge */

export interface MergeResult {
  /** Scenes appended to the target (0 for a revision that brought none, or a stale one). */
  addedScenes: number
  /** Asset ids the target did not have; the loader registers exactly these. */
  addedAssets: string[]
  /** The target's stream after the merge. */
  stream: StreamInfo
}

type InitialPropRow = Bundle['scenes'][number]['initial_props'][number]

/**
 * `next`'s `initial_props` rows for the handles `scene` does not list yet. The backend gives every
 * earlier part a hidden row for each thing a later part brings on stage, so an old part replays
 * without it; a row the scene already has is never changed.
 */
function missingRows(scene: Bundle['scenes'][number], nextScene: Bundle['scenes'][number] | undefined): InitialPropRow[] {
  const rows = nextScene?.initial_props
  if (!Array.isArray(rows) || rows.length === 0) return []
  const have = new Set((scene.initial_props ?? []).map((r) => r.handle))
  return rows.filter((r) => r && typeof r.handle === 'string' && !have.has(r.handle))
}

/** Scene `i` of `target` with `rows` appended — a new object, so a scene shared with `next` is not mutated. */
function withRows(target: Bundle, i: number, rows: readonly InitialPropRow[]): void {
  const scene = target.scenes[i]
  if (!scene || rows.length === 0) return
  target.scenes[i] = { ...scene, initial_props: [...(scene.initial_props ?? []), ...rows] }
}

/**
 * Fold `next` (a newer revision of the same story) into `target`, **in place**.
 *
 * - assets: ids `target` lacks are added; an existing id is never touched (its pack frame is
 *   registered and may be on screen);
 * - scenes: `next.scenes.slice(target.scenes.length)` are pushed; the ones already here are final;
 * - the scenes already here, except `currentScene`, get `next`'s `initial_props` rows for handles
 *   they do not list (add-only);
 * - `stream` becomes `next.stream` (deleted when `next` is complete) and `title` `next.title`;
 * - `cast` / `props` are **not** replaced here: `applyEntities()` does that, together with the
 *   current scene's rows, and the player calls it at the next scene load.
 *
 * A stale revision (a lower `rev` than the one held) changes nothing.
 */
export function mergeGrownBundle(
  target: Bundle,
  next: Bundle,
  opts: { currentScene: number },
): { result: MergeResult; applyEntities: () => void } {
  const had = streamOf(target)
  const got = streamOf(next)
  const noop = { result: { addedScenes: 0, addedAssets: [], stream: had }, applyEntities: () => {} }
  if (!next || typeof next !== 'object' || !Array.isArray(next.scenes)) return noop
  // Out of order (a slow response overtaken by a newer one): keep what we have.
  if (had.state !== 'complete' && got.state !== 'complete' && got.rev < had.rev) return noop
  // A complete story never reopens.
  if (had.state === 'complete' && got.state !== 'complete') return noop

  const addedAssets: string[] = []
  const assets = target.assets as Record<string, Bundle['assets'][string]>
  for (const [id, a] of Object.entries(next.assets ?? {})) {
    if (a === undefined || Object.prototype.hasOwnProperty.call(assets, id)) continue
    assets[id] = a
    addedAssets.push(id)
  }

  const current = Math.round(opts.currentScene)
  const oldCount = target.scenes.length
  for (let i = 0; i < oldCount; i += 1) {
    if (i === current) continue
    withRows(target, i, missingRows(target.scenes[i]!, next.scenes[i]))
  }
  const fresh = next.scenes.slice(oldCount)
  target.scenes.push(...fresh)

  if (next.stream) target.stream = next.stream
  else delete target.stream
  if (typeof next.title === 'string' && next.title.length > 0) target.title = next.title

  const cast = next.cast
  const props = next.props
  const nextCurrent = current >= 0 && current < oldCount ? next.scenes[current] : undefined
  const applyEntities = (): void => {
    if (cast && typeof cast === 'object') target.cast = cast
    if (props && typeof props === 'object') target.props = props
    // The rows the current scene went without while it was on screen.
    const scene = target.scenes[current]
    if (scene && nextCurrent) withRows(target, current, missingRows(scene, nextCurrent))
  }

  return { result: { addedScenes: fresh.length, addedAssets, stream: streamOf(target) }, applyEntities }
}

/* ---------------------------------------------------------------- watch */

export interface BundleWatcher {
  stop(): void
  /** Poll now (the child just reached the end of the ready parts). */
  poke(): void
}

export interface WatchOptions {
  /** Between polls while a part plays. */
  intervalMs?: number
  /** Between polls while the child waits at the end of the ready parts. */
  fastIntervalMs?: number
  /**
   * The ceiling of the error backoff — and the pace whenever nothing is coming soon: a `paused`
   * stream (for hours), one whose every part is already here, or one silent for `stallMs`.
   */
  maxBackoffMs?: number
  /**
   * No new revision for this long: the story may have stopped without a rewrite (a cancelled job),
   * so even a waiting child is polled at `maxBackoffMs` instead of every second.
   */
  stallMs?: number
  /** Wall ms, for the stall; `Date.now` by default. */
  now?: () => number
  fetchImpl?: typeof fetch
  isWaiting?: () => boolean
  onBundle: (b: unknown) => void
  /** `bundle.json` is gone (404 / 410): the story was deleted. */
  onGone?: () => void
}

export const WATCH_INTERVAL_MS = 2000
export const WATCH_FAST_INTERVAL_MS = 1000
export const WATCH_MAX_BACKOFF_MS = 10_000
/**
 * A story still being made that has shown nothing new for this long has probably stopped without
 * saying so (a job cancelled while it streamed leaves `state: streaming` behind): the poll slows to
 * its ceiling, and a child waiting at the end of the ready parts is offered "End here".
 */
export const STREAM_STALL_MS = 180_000

/**
 * Poll `url` for newer revisions of a story being made. Each poll is
 * `fetch(url, { cache: 'no-store', headers: { 'If-None-Match': lastEtag } })`: a 304 means nothing
 * changed, a 200 hands the parsed bundle to `onBundle`, a 404 calls `onGone`. It stops by itself
 * when a received bundle has no open stream, on `onGone`, or on `stop()`; a failed request (the
 * network, a 5xx, a half-written body) doubles the wait up to `maxBackoffMs` and tries again.
 */
export function watchBundle(url: string, opts: WatchOptions): BundleWatcher {
  const intervalMs = opts.intervalMs ?? WATCH_INTERVAL_MS
  const fastIntervalMs = opts.fastIntervalMs ?? WATCH_FAST_INTERVAL_MS
  const maxBackoffMs = Math.max(intervalMs, opts.maxBackoffMs ?? WATCH_MAX_BACKOFF_MS)
  const stallMs = opts.stallMs ?? STREAM_STALL_MS
  const clock = opts.now ?? (() => Date.now())
  const doFetch = opts.fetchImpl ?? ((input: RequestInfo | URL, init?: RequestInit) => fetch(input, init))

  let etag: string | null = null
  let stopped = false
  let busy = false
  let again = false
  let errors = 0
  /** Nothing is coming soon: the last revision was `paused`, or every part is already here. */
  let slow = false
  /** Wall ms of the last revision taken (or of the start). */
  let changedAt = clock()
  let timer: ReturnType<typeof setTimeout> | null = null

  const clear = (): void => {
    if (timer !== null) clearTimeout(timer)
    timer = null
  }
  const schedule = (ms: number): void => {
    clear()
    if (!stopped) timer = setTimeout(() => void poll(), Math.max(0, ms))
  }
  const delay = (): number => {
    if (errors > 0) return Math.min(maxBackoffMs, intervalMs * 2 ** errors)
    // Before the waiting child's fast pace: a paused story goes on in minutes or hours, one with
    // every part here waits only for its final rewrite, and a silent one may never change again.
    // `poke()` still looks at once when the child reaches the wait.
    if (slow || clock() - changedAt >= stallMs) return maxBackoffMs
    if (opts.isWaiting?.()) return fastIntervalMs
    return intervalMs
  }
  const stop = (): void => {
    stopped = true
    clear()
  }

  async function poll(): Promise<void> {
    timer = null
    if (stopped) return
    if (busy) {
      again = true
      return
    }
    busy = true
    try {
      const res = await doFetch(url, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: etag ? { 'If-None-Match': etag } : {},
      })
      if (stopped) return
      if (res.status === 304) {
        errors = 0
      } else if (res.status === 404 || res.status === 410) {
        stop()
        opts.onGone?.()
        return
      } else if (!res.ok) {
        errors += 1
      } else {
        const body: unknown = await res.json()
        if (stopped) return
        const s = streamOf((body ?? {}) as Pick<Bundle, 'scenes'>)
        opts.onBundle(body)
        // Only once it was taken: a revision the player choked on (the final one too) is fetched
        // whole again.
        if (!isOpen(s)) stopped = true
        slow = s.state === 'paused' || !expectsMore(s)
        changedAt = clock()
        etag = res.headers?.get?.('ETag') ?? null
        errors = 0
        if (stopped) {
          clear()
          return
        }
      }
    } catch {
      // offline for a moment, or a body cut short: the next poll gets the whole of it
      errors += 1
    } finally {
      busy = false
    }
    if (stopped) return
    if (again) {
      again = false
      schedule(0)
      return
    }
    schedule(delay())
  }

  schedule(intervalMs)
  return {
    stop,
    poke() {
      if (stopped) return
      if (busy) again = true
      else schedule(0)
    },
  }
}
